import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { LineReveal } from "../motion/Reveal";
import { Phone, MessageCircle, ArrowDown } from "lucide-react";
import { useLang, UI } from "@/lib/lang";
import { optimizeUrl, buildSrcSet } from "@/lib/img";
import { mediaUrl } from "@/lib/api";

export default function Hero({ brand, hero }) {
  const { t } = useLang();
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const { scrollY } = useScroll();
  const logoY = useTransform(scrollY, [0, 800], [0, 140]);
  const logoScale = useTransform(scrollY, [0, 800], [1, 1.05]);
  const bgY = useTransform(scrollY, [0, 800], [0, 200]);

  const carousel = (hero?.carousel || []).filter((s) => s?.url);
  const [carIdx, setCarIdx] = useState(0);
  const [carTick, setCarTick] = useState(0); // resets the auto-rotate timer on manual change

  useEffect(() => {
    if (carousel.length < 2) return;
    const id = setInterval(
      () => setCarIdx((v) => (v + 1) % carousel.length),
      4500,
    );
    return () => clearInterval(id);
  }, [carousel.length, carTick]);

  const gotoSlide = (i) => {
    setCarIdx(i);
    setCarTick((t) => t + 1);
  };

  useEffect(() => {
    const onMove = (e) => {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      setMouse({ x: (e.clientX - cx) / cx, y: (e.clientY - cy) / cy });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#0A0A0A]"
    >
      {/* Photo carousel (background) — heavily blurred abstract color wash to prevent perceived flash */}
      <div className="absolute inset-0 z-0 overflow-hidden" data-testid="hero-carousel">
        {carousel.map((slide, i) => (
          <motion.div
            key={i}
            initial={false}
            animate={{ opacity: i === carIdx ? 1 : 0 }}
            transition={{ duration: 3.5, ease: "linear" }}
            className="absolute inset-0"
            style={{ willChange: "opacity" }}
          >
            <img
              src={optimizeUrl(mediaUrl(slide.url), 800)}
              alt=""
              className="w-full h-full object-cover scale-125"
              loading={i === 0 ? "eager" : "lazy"}
              decoding="async"
              fetchPriority={i === 0 ? "high" : "auto"}
              style={{
                filter:
                  "blur(28px) brightness(0.42) saturate(0.55) contrast(0.95)",
              }}
            />
          </motion.div>
        ))}
        {/* dominant unified colour wash so photo variance doesn't leak through */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 90% 70% at 50% 30%, rgba(10,15,25,0.55), rgba(6,8,14,0.95) 75%)",
          }}
        />
        <div className="absolute inset-0 bg-black/45" />
      </div>

      {/* Blueprint grid overlay */}
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 z-[1] grid-blueprint opacity-25 pointer-events-none"
      />
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className="absolute inset-0 z-[1] grid-blueprint-fine origin-left opacity-40 pointer-events-none"
      />

      {/* Radial glow */}
      <div className="absolute top-[20%] left-1/2 -translate-x-1/2 w-[900px] h-[900px] glow-blue pointer-events-none z-[1]" />
      <div className="absolute inset-6 md:inset-10 border border-white/[0.06] pointer-events-none z-[1]" />

      {/* Content grid */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 md:px-14 pt-28 md:pt-40 pb-16 md:pb-24">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="num-tick mb-6 flex items-center gap-3"
              data-testid="hero-eyebrow"
            >
              <span className="inline-block w-8 h-px bg-[#1E7FFF]" />
              {t(hero?.eyebrow)}
            </motion.p>

            <h1
              className="font-display text-white uppercase text-[11vw] sm:text-[12vw] md:text-[9.5vw] lg:text-[8.5vw] tracking-tight"
              data-testid="hero-headline"
            >
              <LineReveal
                text={t(hero?.line1) || "We remodel"}
                delay={0.5}
                el="span"
                className="block"
              />
              <span className="block">
                <span className="txt-stroke">
                  <LineReveal text={t(hero?.line2) || "your home."} delay={0.75} />
                </span>
              </span>
              <span className="block text-[#1E7FFF] txt-shadow-blue">
                <LineReveal text={t(hero?.line3) || "We clean"} delay={1.0} />
              </span>
              <span className="block">
                <LineReveal
                  text={t(hero?.line4) || "every last detail."}
                  delay={1.25}
                />
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.7 }}
              className="mt-10 max-w-xl text-zinc-300 text-base md:text-lg leading-relaxed font-body"
              data-testid="hero-subcopy"
            >
              {t(hero?.sub)}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.9 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <a href="#contact" className="btn-primary" data-testid="hero-cta-quote">
                <Phone size={14} strokeWidth={2} />
                {t(hero?.cta_primary) || "Get a quote"}
              </a>
              <a href="#work" className="btn-ghost" data-testid="hero-cta-work">
                {t(hero?.cta_secondary) || "See our work"}
                <ArrowDown size={14} strokeWidth={2} />
              </a>
            </motion.div>

            {/* carousel dots + caption */}
            {carousel.length > 1 && (
              <div
                className="mt-10 flex items-center gap-4 flex-wrap"
                data-testid="hero-carousel-caption"
              >
                <div className="flex items-center gap-2">
                  {carousel.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => gotoSlide(i)}
                      aria-label={`Slide ${i + 1}`}
                      className={`h-1.5 transition-all duration-500 ${
                        i === carIdx
                          ? "bg-[#1E7FFF] w-8"
                          : "bg-white/25 w-4 hover:bg-white/60"
                      }`}
                      data-testid={`hero-carousel-dot-${i}`}
                    />
                  ))}
                </div>
                <AnimatePresence mode="wait">
                  <motion.p
                    key={carIdx}
                    initial={{ opacity: 0, x: 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -8 }}
                    transition={{ duration: 0.5 }}
                    className="num-tick text-white/70"
                  >
                    {String(carIdx + 1).padStart(2, "0")} ·{" "}
                    {t(carousel[carIdx]?.caption)}
                  </motion.p>
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Right: kinetic logo */}
          <div className="col-span-12 md:col-span-5 flex items-center justify-center md:justify-end mt-6 md:mt-0 mb-8 md:mb-0">
            <div className="relative w-full max-w-[260px] md:max-w-[520px] aspect-square">
              {/* Outer counter-rotating dashed ring */}
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 80, repeat: Infinity, ease: "linear" }}
                className="absolute -inset-8 md:-inset-10 rounded-full border border-dashed border-white/15"
              />
              {/* Inner rotating ring */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
                className="absolute -inset-4 md:-inset-6 rounded-full border border-[#1E7FFF]/25"
              />
              {/* Static soft halo — no pulsing */}
              <div
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle, rgba(30,127,255,0.32) 0%, transparent 60%)",
                  filter: "blur(30px)",
                }}
              />
              {/* Corner ticks */}
              <div className="corner-ticks" />

              {/* Orbiting sparks (desktop only — they extend far outside on mobile) */}
              <div className="hidden md:block">
                {[0, 1, 2, 3].map((n) => (
                  <motion.div
                    key={n}
                    className="absolute top-1/2 left-1/2 w-2 h-2 -mt-1 -ml-1 pointer-events-none"
                    animate={{ rotate: 360 }}
                    transition={{
                      duration: 20 + n * 4,
                      repeat: Infinity,
                      ease: "linear",
                      delay: n * 0.4,
                    }}
                  >
                    <div
                      className={`absolute top-0 left-0 w-full h-full rounded-full ${
                        n % 2 === 0 ? "bg-[#1E7FFF]" : "bg-white"
                      } shadow-[0_0_18px_rgba(30,127,255,0.9)]`}
                      style={{
                        transform: `translate(${n % 2 === 0 ? "220px" : "-220px"}, ${n < 2 ? "-40px" : "40px"})`,
                      }}
                    />
                  </motion.div>
                ))}
              </div>

              {/* Logo: parallax + tilt + slow float + slow rotate + breathing scale */}
              <motion.div
                style={{
                  y: logoY,
                  scale: logoScale,
                  rotateY: mouse.x * 10,
                  rotateX: -mouse.y * 10,
                }}
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-4 md:inset-6 will-change-transform"
              >
                <motion.div
                  animate={{
                    y: [0, -14, 0],
                    rotate: [-1.5, 1.5, -1.5],
                  }}
                  transition={{
                    y: { duration: 5, repeat: Infinity, ease: "easeInOut" },
                    rotate: { duration: 9, repeat: Infinity, ease: "easeInOut" },
                  }}
                  className="w-full h-full"
                >
                  <motion.img
                    src={brand?.logo}
                    alt={brand?.name || "El Cuquito de Raleigh"}
                    className="w-full h-full object-contain drop-shadow-[0_25px_60px_rgba(30,127,255,0.45)]"
                    data-testid="hero-logo"
                  />
                </motion.div>
              </motion.div>

              <div className="absolute -top-2 -left-2 num-tick">[ 01 ]</div>
              <div className="absolute -bottom-2 -right-2 num-tick text-[#1E7FFF] hidden sm:block">
                REMODELING · CLEANING
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar (desktop only) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 0.8 }}
        className="hidden md:flex absolute bottom-12 left-14 right-14 justify-between items-end num-tick z-10 gap-8"
      >
        <div className="flex items-center gap-6">
          <span className="text-white/70">
            <span className="text-[#1E7FFF]">↓</span>&nbsp;&nbsp;{t(UI.scroll)}
          </span>
          <span className="hidden md:inline">{t(UI.bilingual)}</span>
        </div>
        <div className="flex flex-row flex-wrap md:items-center gap-x-4 gap-y-2 md:gap-8">
          <a
            href={`tel:${(brand?.phone || "").replace(/\s/g, "")}`}
            className="hover:text-[#1E7FFF] transition-colors flex items-center gap-2"
            data-testid="hero-phone"
          >
            <Phone size={11} /> {brand?.phone}
          </a>
          <a
            href={`https://wa.me/1${(brand?.whatsapp || "").replace(/\D/g, "")}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#25D366] transition-colors flex items-center gap-2"
            data-testid="hero-whatsapp"
          >
            <MessageCircle size={11} /> WA {brand?.whatsapp_display}
          </a>
        </div>
      </motion.div>
    </section>
  );
}
